// frontend/src/pages/ClipsPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const MAX_FEATURED = 6;

const ClipsPage = () => {
  const { isLoggedIn, authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [clips, setClips]       = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState(null);

  const font = {
    mono: '"JetBrains Mono", monospace',
    condensed: '"Barlow Condensed", sans-serif',
    body: '"Barlow", sans-serif',
  };

  useEffect(() => {
    if (!authToken) { navigate('/account'); return; }
    const load = async () => {
      try {
        const [clipsRes, featuredRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/twitch/clips`,        { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`${BACKEND_URL}/api/user/featured-clips`, { headers: { Authorization: `Bearer ${authToken}` } }),
        ]);
        const [clipsData, featuredData] = await Promise.all([clipsRes.json(), featuredRes.json()]);
        setClips(clipsData.clips || []);
        setFeatured(featuredData.clip_ids || []);
      } catch {
        setError('Could not load clips.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authToken]);

  const toggle = (id) => {
    setSaved(false);
    setFeatured(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_FEATURED) return prev;
      return [...prev, id];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/featured-clips`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clip_ids: featured }),
      });
      if (!res.ok) throw new Error();
      setSaved(true);
    } catch {
      setError('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate   = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const formatViews  = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'K' : String(n || 0);

  // Featured first, then the rest
  const sorted = [
    ...featured.map(id => clips.find(c => c.id === id)).filter(Boolean),
    ...clips.filter(c => !featured.includes(c.id)),
  ];

  const S = {
    root:    { background: '#000', minHeight: '100vh', color: '#fff', fontFamily: font.body },
    topBar:  { borderBottom: '1px solid #1a1a1a', padding: '0.75rem 2.5rem', display: 'flex', alignItems: 'center', gap: '1rem' },
    backBtn: { background: 'none', border: 'none', color: '#666', fontFamily: font.mono, fontSize: '0.65rem', letterSpacing: '0.1em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
    page:    { maxWidth: 1100, margin: '0 auto', padding: '2.5rem 2rem' },
  };

  if (loading) return (
    <div style={{ ...S.root, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 36, height: 36, border: '2px solid #222', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
        <p style={{ fontFamily: font.mono, fontSize: '0.7rem', color: '#555', letterSpacing: '0.1em' }}>LOADING CLIPS...</p>
      </div>
    </div>
  );

  return (
    <div style={S.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;900&family=Barlow:wght@300;400&family=JetBrains+Mono:wght@400;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        .bgn-clip:hover { border-color: #555 !important; }
        .bgn-clip.featured:hover { border-color: #fff !important; }
        .bgn-clip:hover img { filter: grayscale(0%) !important; }
        .bgn-back:hover { color: #fff !important; }
      `}</style>

      {/* Top bar */}
      <div style={S.topBar}>
        <button className="bgn-back" style={S.backBtn} onClick={() => navigate('/account')}>
          <ArrowLeft size={13} /> BACK TO DASHBOARD
        </button>
      </div>

      <div style={S.page}>

        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontFamily: font.mono, fontSize: '0.6rem', color: '#444', letterSpacing: '0.2em', marginBottom: 6 }}>// MANAGE CLIPS</div>
            <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '-0.02em', lineHeight: 1 }}>Featured Clips</div>
            <p style={{ fontFamily: font.body, fontWeight: 300, fontSize: '0.85rem', color: '#555', marginTop: 6 }}>
              Select up to {MAX_FEATURED} clips to feature at the top of your public profile. Click to select or deselect.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
            <span style={{ fontFamily: font.mono, fontSize: '0.7rem', color: featured.length >= MAX_FEATURED ? '#ff4444' : '#555', letterSpacing: '0.1em' }}>
              {featured.length} / {MAX_FEATURED} SELECTED
            </span>
            <button
              onClick={save}
              disabled={saving}
              style={{ background: '#fff', color: '#000', border: 'none', padding: '0.65rem 1.75rem', fontFamily: font.mono, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'SAVING...' : 'SAVE'}
            </button>
            {saved  && <span style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#4caf50', letterSpacing: '0.1em' }}>✓ SAVED</span>}
            {error  && <span style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#ff4444' }}>{error}</span>}
          </div>
        </div>

        {clips.length === 0 ? (
          <div style={{ background: '#111', border: '1px solid #222', padding: '4rem', textAlign: 'center' }}>
            <p style={{ fontFamily: font.mono, fontSize: '0.7rem', color: '#444', letterSpacing: '0.1em' }}>NO CLIPS FOUND</p>
            <p style={{ fontFamily: font.body, fontSize: '0.8rem', color: '#555', marginTop: 6 }}>Create clips on Twitch and they'll appear here.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
            {sorted.map(clip => {
              const isFeatured = featured.includes(clip.id);
              const rank = featured.indexOf(clip.id) + 1;
              const atMax = featured.length >= MAX_FEATURED && !isFeatured;
              return (
                <div
                  key={clip.id}
                  className={`bgn-clip${isFeatured ? ' featured' : ''}`}
                  style={{ position: 'relative', background: '#111', border: `1px solid ${isFeatured ? '#fff' : '#222'}`, cursor: atMax ? 'not-allowed' : 'pointer', overflow: 'hidden', opacity: atMax ? 0.4 : 1, transition: 'border-color 0.15s' }}
                  onClick={() => !atMax && toggle(clip.id)}
                  title={atMax ? `Max ${MAX_FEATURED} selected` : isFeatured ? 'Click to remove' : 'Click to feature'}
                >
                  <img src={clip.thumbnail_url} alt={clip.title} style={{ width: '100%', display: 'block', filter: isFeatured ? 'grayscale(20%)' : 'grayscale(70%)', transition: 'filter 0.2s' }} />

                  {/* Featured badge */}
                  <div style={{ position: 'absolute', top: 8, left: 8, background: isFeatured ? '#fff' : 'rgba(0,0,0,0.7)', color: isFeatured ? '#000' : '#888', fontFamily: font.mono, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.15em', padding: '3px 8px', border: isFeatured ? 'none' : '1px solid #333' }}>
                    {isFeatured ? `#${rank} FEATURED` : 'NOT FEATURED'}
                  </div>

                  <div style={{ padding: '0.6rem 0.75rem' }}>
                    <div style={{ fontFamily: font.body, fontSize: '0.82rem', color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>{clip.title || 'Untitled'}</div>
                    <div style={{ display: 'flex', gap: '0.75rem', fontFamily: font.mono, fontSize: '0.6rem', color: '#555' }}>
                      <span>{formatViews(clip.view_count)} views</span>
                      <span>{formatDate(clip.created_at)}</span>
                      {clip.game_name && <span>{clip.game_name}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Save button bottom */}
        {clips.length > 0 && (
          <div style={{ marginTop: '2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={save} disabled={saving} style={{ background: '#fff', color: '#000', border: 'none', padding: '0.75rem 2rem', fontFamily: font.mono, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'SAVING...' : 'SAVE FEATURED CLIPS'}
            </button>
            {saved && <span style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#4caf50', letterSpacing: '0.1em' }}>✓ SAVED</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClipsPage;